import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, FileDown, ClipboardCheck, Sparkles, AlertCircle } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const BADGE = (s) =>
    s === "PASS" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
    : s === "LIVE"? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
    : s === "DEMO"? "border-amber-500/40   bg-amber-500/10   text-amber-400"
                  : "border-red-500/40     bg-red-500/10     text-red-400";

export default function AcceptanceAudit() {
    const [audit, setAudit] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/edi/acceptance-audit");
            setAudit(data);
        } catch { toast.error("Acceptance Audit failed"); }
        finally { setBusy(false); }
    };

    const download = async (fmt) => {
        if (!audit) return;
        setDownloading(true);
        try {
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${API_BASE}/reports/export`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    title: "SGS Bateman EPCM360 · Executive Acceptance Report",
                    content: audit.report_markdown,
                    fmt,
                    confidentiality: "Board",
                }),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : `SGSBateman_Acceptance_Report.${fmt}`;
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
            toast.success(`Acceptance Report · ${fmt.toUpperCase()} downloaded`);
        } catch { toast.error(`Acceptance Report · ${fmt.toUpperCase()} export failed`); }
        finally { setDownloading(false); }
    };

    return (
        <div className="space-y-8" data-testid={EDI.acceptanceAuditRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Executive Acceptance Audit™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        A live verification pass across every menu, dashboard, workflow, AI
                        response, export, integration, and regression check — outputs a signed
                        Executive Acceptance Report ready for board circulation.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">Signed board-ready report</span>
                </div>
            </div>

            {audit && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        ["Checks executed", audit.total_checks.toLocaleString()],
                        ["Passed",          audit.passed.toLocaleString()],
                        ["Coverage",        `${audit.coverage_pct}%`],
                        ["SharePoint",      audit.sharepoint_mode.toUpperCase()],
                    ].map(([k, v]) => (
                        <div key={k} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                            <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                            <div className="kicker text-slate-400 text-[10px]">{k}</div>
                            <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="glass rounded-xl p-6">
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        data-testid={EDI.acceptanceAuditRun}
                        onClick={run} disabled={busy}
                        className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-5 py-2.5 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                    >
                        <ClipboardCheck className="h-4 w-4" strokeWidth={2} />
                        {busy ? "Verifying…" : "Re-run acceptance audit"}
                    </button>
                    {[["pdf","PDF"],["docx","Word"],["pptx","PowerPoint"]].map(([fmt, label]) => (
                        <button key={fmt}
                            data-testid={EDI.acceptanceAuditExport(fmt)}
                            onClick={() => download(fmt)} disabled={!audit || downloading}
                            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold)]/10 disabled:opacity-40 transition-colors duration-200"
                        >
                            <FileDown className="h-3.5 w-3.5" strokeWidth={2} />
                            Signed Report · {label}
                        </button>
                    ))}
                </div>
            </div>

            {audit && (
                <div className="glass rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <h3 className="font-display font-black text-white tracking-tight">Scope of verification</h3>
                        <div className="flex-1" />
                        <span className="kicker text-[10px] text-slate-500">{audit.sections.length} sections</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm font-body">
                            <thead>
                                <tr className="text-slate-500 kicker text-[10px]">
                                    <th className="text-left px-4 py-3">Section</th>
                                    <th className="text-right px-4 py-3">Passed</th>
                                    <th className="text-right px-4 py-3">Checks</th>
                                    <th className="text-right px-4 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {audit.sections.map((c) => (
                                    <tr key={c.section} className="border-t border-slate-800/60">
                                        <td className="px-4 py-2 text-slate-100">
                                            <div>{c.section}</div>
                                            {c.note && (
                                                <div className="mt-1 text-[11px] text-amber-400 font-body inline-flex items-center gap-1">
                                                    <AlertCircle className="h-3 w-3" strokeWidth={2} />
                                                    {c.note}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-right text-emerald-400 font-display font-bold">{c.passed}</td>
                                        <td className="px-4 py-2 text-right text-slate-300">{c.checks}</td>
                                        <td className="px-4 py-2 text-right">
                                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] border ${BADGE(c.status)}`}>{c.status}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
